import { InjectionToken } from '@angular/core';

export interface AvailableFeature {
  name: string;
  description: string;
}

/**
 * Configuration that determines how a payment card number should be formatted.
 *
 * @remarks
 * This interface defines the structure for formatting payment card numbers, including
 * total length, masking behavior, and segmentation for display purposes. It's commonly
 * used with the {@link PaymentCardNumberPipe} and payment card components.
 *
 * @example
 *
 * ```html
 * <bb-payment-card-ui
 *   [paymentCardNumberFormat]="{
 *     length: 19,
 *     maskRange: [0, 15],
 *     segments: 4
 *   }">
 * </bb-payment-card-ui>
 * ```
 */
export interface PaymentCardNumberFormat {
  /**
   * Total length of the payment card number.
   */
  length: number;
  /**
   * Index range of characters that should be masked.
   */
  maskRange: [number, number];
  /**
   * The size (or array of sizes) the segments should have.
   */
  segments: number | Array<number>;
}

/**
 * Default payment card number format configuration.
 *
 * @remarks
 * Provides standard formatting for 16-digit payment cards with masking of the first 12 digits
 * and segmentation into groups of 4 digits. This format is suitable for most major credit
 * and debit card types including Visa, Mastercard, and Discover.
 *
 * @example
 *
 * ```typescript
 * @Input() paymentCardNumberFormat: PaymentCardNumberFormat = DefaultPaymentCardNumberFormat;
 * ```
 */
export const DefaultPaymentCardNumberFormat: PaymentCardNumberFormat = {
  length: 16,
  maskRange: [0, 12],
  segments: 4,
};

/**
 * Lookup object containing payment card number formats for common payment card vendors.
 *
 * @remarks
 * Pre-configured formatting rules for different payment card types. Each vendor entry
 * specifies the appropriate length, masking range, and segmentation for that card type.
 * American Express cards use a different format with 15 digits and custom segmentation.
 *
 * @example
 *
 * ```typescript
 * const amexFormat = PaymentCardNumberVendors['american-express'];
 * // Results in: { length: 15, maskRange: [0, 11], segments: [4, 6, 5] }
 *
 * const visaFormat = PaymentCardNumberVendors.visa;
 * // Results in: { length: 16, maskRange: [0, 12], segments: 4 }
 * ```
 */
export const PaymentCardNumberVendors: { [vendor: string]: PaymentCardNumberFormat } = {
  mastercard: {
    length: 16,
    maskRange: [0, 12],
    segments: 4,
  },
  visa: {
    length: 16,
    maskRange: [0, 12],
    segments: 4,
  },
  'visa-debit': {
    length: 16,
    maskRange: [0, 12],
    segments: 4,
  },
  'diners-club': {
    length: 16,
    maskRange: [0, 12],
    segments: 4,
  },
  'american-express': {
    length: 15,
    maskRange: [0, 11],
    segments: [4, 6, 5],
  },
};

/**
 * A token that enables numbers masking synchronization with backend
 * If set FE should not apply any masking and rely on the backend masking behavior
 *
 * @internal
 */
export const SYNCHRONIZE_NUMBERS_MASKING: InjectionToken<boolean> = new InjectionToken<boolean>(
  'Enables accounts masking synchronization with backend',
  {
    providedIn: 'root',
    factory() {
      return false;
    },
  },
);
